import { useCallback, useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  Box,
  Grid,
  Typography,
  Card,
  CardContent,
  CardActionArea,
  Divider,
} from '@mui/material';
import { PageTemplate } from 'src/shared/templates/pageTemplate';
import { ContentPageAPI } from 'src/shared/services/api/contentPageAPI';
import SkeletonCards from 'src/shared/components/skeletons/skeletonCards';
import { IParams } from 'src/shared/interfaces/params';
import { ContentPageResponse } from 'src/shared/services/api/response/contentPageResponse';
import { ActionButtons } from 'src/shared/components/atoms/actionButtons';
import { fileUrl } from 'src/shared/services/utils/url';
import { GenerateActionButtonIcons } from 'src/shared/components/atoms/generateActionButtonsIcons';
import ConsoleHelper from 'src/shared/helpers/consoleHelper';
import { RootStateType } from 'src/shared/services/redux/reducers';
import { GetAdminPermission } from 'src/shared/helpers/permissionHelper';
import { useTranslation } from 'react-i18next';
import {
  DescriptionOutlined,
  InfoOutlined,
  InsertDriveFileOutlined,
  PhotoAlbum,
} from '@mui/icons-material';

const MenuPage = () => {
  const { id } = useParams<IParams>();
  const { t } = useTranslation();
  // Global states
  const { user } = useSelector((state: RootStateType) => state.oidc);

  // Local states
  const [loading, setLoading] = useState<boolean>(true);
  const [item, setItem] = useState<ContentPageResponse | undefined>();
  const [selectedImage, setSelectedImage] = useState<string | undefined>();

  // Helper function to decode HTML entities and remove HTML tags
  const cleanContent = (html: string): string => {
    const textarea = document.createElement('textarea');
    textarea.innerHTML = html;
    return textarea.value;
  };

  const getContentPageById = useCallback(async () => {
    setLoading(true);
    try {
      const contentApi = new ContentPageAPI();
      const res = await contentApi.Get(id);
      if (res) {
        setItem(res?.result);
        await setSelectedImage(
          fileUrl(res?.result?.featuredImageDocumentId, user?.access_token),
        );
        setLoading(false);
      }
    } catch (error) {
      setLoading(false);
    }
  }, [id, user?.access_token]);

  useEffect(() => {
    getContentPageById();
  }, [getContentPageById]);

  // Visa ikon för respektive sidtyp
  const getIcon = (type: string) => {
    let iconToShow = <InfoOutlined />;
    if (type === 'DocumentListPage') iconToShow = <DescriptionOutlined />;
    if (type === 'GalleryListPage') iconToShow = <PhotoAlbum />;
    if (type === 'GalleryPage') iconToShow = <PhotoAlbum />;
    if (type === 'MenuPage') iconToShow = <InsertDriveFileOutlined />;

    return iconToShow;
  };

  // Check if page is a parent without content
  const isParentThatHasChildren =
    item?.canHaveChildren && item?.childItems && item.childItems.length > 0;

  // const isParentWithoutContent =
  //   item?.canHaveChildren &&
  //   (!item?.content || item.content.trim() === '') &&
  //   item?.childItems &&
  //   item.childItems.length > 0;

  return (
    <PageTemplate
      header={item?.title ?? ''}
      featuredImage={selectedImage}
      featuredImageText={item?.featuredImageText ?? ''}
      onImageError={() => setSelectedImage(undefined)}
      headerContent={item?.content ?? ''}
      showTags
      tags={item?.tags}
      businessAreaTags={item?.businessAreaTags}
      content={
        <>
          {loading && <SkeletonCards rows={1} />}
          {!loading && item && (
            <>
              {/* Show child items grid if page is parent without content */}
              {isParentThatHasChildren && (
                <>
                  <Divider />
                  <Box my={2}>
                    <Grid container spacing={2}>
                      {item.childItems?.map(
                        (childItem: ContentPageResponse) => (
                          <Grid item xs={12} sm={6} md={4} key={childItem.id}>
                            <Card
                              sx={{
                                height: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                boxShadow:
                                  'rgba(99, 99, 99, 0.2) 0px 2px 8px 0px',
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                  boxShadow:
                                    'rgba(99, 99, 99, 0.2) 0px 2px 8px 0px',
                                  transform: 'translateY(-4px)',
                                },
                              }}
                            >
                              <CardActionArea
                                component={Link}
                                to={`/${childItem.contentType}/${childItem.id}`}
                                sx={{
                                  flexGrow: 1,
                                  display: 'flex',
                                  flexDirection: 'column',
                                }}
                              >
                                <CardContent
                                  sx={{ flexGrow: 1, width: '100%' }}
                                >
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'flex-start',
                                      gap: 1,
                                      mb: 1,
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        color: 'primary.main',
                                        display: 'flex',
                                        flexShrink: 0,
                                      }}
                                    >
                                      {getIcon(childItem.contentType)}
                                    </Box>
                                    <Typography
                                      gutterBottom
                                      variant='body2'
                                      component='h3'
                                      sx={{
                                        color: 'primary.main',
                                        mb: 0,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        display: '-webkit-box',
                                        WebkitLineClamp: 2,
                                        WebkitBoxOrient: 'vertical',
                                      }}
                                    >
                                      {childItem.title ?? 'Untitled'}
                                    </Typography>
                                  </Box>
                                  {childItem.content && (
                                    <Typography
                                      variant='subtitle1'
                                      // color='textSecondary'
                                      sx={{
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        display: '-webkit-box',
                                        WebkitLineClamp: 2,
                                        WebkitBoxOrient: 'vertical',
                                      }}
                                    >
                                      {cleanContent(
                                        childItem.content.replace(
                                          /<[^>]*>/g,
                                          '',
                                        ),
                                      )}
                                    </Typography>
                                  )}
                                </CardContent>
                              </CardActionArea>
                            </Card>
                          </Grid>
                        ),
                      )}
                    </Grid>
                  </Box>
                </>
              )}
            </>
          )}
          <ActionButtons
            items={GenerateActionButtonIcons(
              // Create
              undefined,
              // Edit
              GetAdminPermission(item?.companyId)
                ? `/MenuPage/${id}/edit`
                : undefined,
              // Pin
              GetAdminPermission(item?.companyId)
                ? () => ConsoleHelper.log('Pin item', id)
                : undefined,
            )}
          />
        </>
      }
    />
  );
};

export default MenuPage;
